import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3EducationPageState from './useTreatmentPlanV3EducationPageState';

import type { AppDispatch } from '../../../../../../store';
import type { EducationPayload } from '../../../../../common';
import type {
  TreatmentPlanV3EducationDependencies,
  UseTreatmentPlanV3EducationResult,
} from './types';

export default function useTreatmentPlanV3Education() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3EducationPageState();

  return useTreatmentPlanV3EducationWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3EducationWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  educationInfo,
  notAttendingSchool,
  isLoading,
  hasLoaded,
  hasLoadError,
  isSaving,
  isUpdating,
}: TreatmentPlanV3EducationDependencies): UseTreatmentPlanV3EducationResult {
  const update = useCallback(
    async (data: EducationPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3EducationInfo({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3EducationInfo.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const setNotAttendingSchool = useCallback(
    async (nextNotAttendingSchool: boolean) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.setTreatmentPlanV3NotAttendingSchool({
          treatmentPlanId,
          notAttendingSchool: nextNotAttendingSchool,
          getAccessTokenSilently,
        }),
      );
      return actions.setTreatmentPlanV3NotAttendingSchool.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    educationInfo,
    notAttendingSchool,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isUpdating,
    update,
    setNotAttendingSchool,
  };
}
